import React, { ReactElement, useContext, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PieChart } from 'react-minimal-pie-chart';
import Wootric from '../components/Wootric';
import { usePlayer }  from '../contexts/usePlayer';
import { PlayerStorage } from '../localstorage/PlayerStorage';
import { StoryPoint } from '../types/StoryPoint';
import { UserContext } from '../contexts/UserContext';
import { PlanType } from '../api/models/User';

const Container = styled(Grid)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const PieChartContainer = styled(Grid)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
}));

export interface PlayerEstimate {
    playerName: string;
    storyPoint?: StoryPoint;
}

export interface EstimationResultProp {
    playerEstimates: PlayerEstimate[];
    roundCount: number;
}

function groupBy<T, K>(list: T[], getKey: (item: T) => K) {
    const map = new Map<K, T[]>();
    list.forEach((item) => {
        const key = getKey(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return Array.from(map.values());
}

/*
* @param playerEstimates playerestimates from properties$
* filters out empty estimates
* group the esimates by players giving the same estimate
* sorts by number of plauers giving  the same estimate so that they are shown first
*/
function calcPieData(playerEstimates: PlayerEstimate[]) {
    const esimates = playerEstimates.filter(pe => pe.storyPoint);
    const pieData = groupBy(esimates, (pe) => pe.storyPoint);
    pieData.sort((a, b) => b.length - a.length);
    return pieData;
}

export default function EstimationResult(props: EstimationResultProp): ReactElement {

    const theme = useTheme();
    const player = usePlayer();
    const { user } = useContext(UserContext);
    const pieData = calcPieData(props.playerEstimates);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const pieColors = theme.chart.pieColors.map(c => c!.toString());

    // Memoize the pie chart data to prevent unnecessary re-renders that break animation
    const chartData = useMemo(() => {
        let i = 0;
        return pieData.map(entry => ({ 
            title: entry[0].storyPoint, 
            value: entry.length, 
            color: pieColors[Math.min(pieColors.length - 1, i++)] 
        }));
    }, [pieData, pieColors]);
 
    function renderWootric() {
        const playerStorage = new PlayerStorage();
        const originalPlayerId = playerStorage.GetOriginalPlayerId();
        const userIdentifier = user ? user.idPId : originalPlayerId != null ? originalPlayerId : player.player ? player.player.name : "unknown";
        const originalCreatedAt = playerStorage.GetOriginalPlayerCreatedAt();
        const userCreatedAt = originalCreatedAt != null ? originalCreatedAt : new Date();
        const userName = player.player ? player.player.name : "unknown";
        const isModerator = player.player ? player.player.isModerator : false;
        const isRegistered = user != null;
        const planType = user ? PlanType[user.plan.type] : undefined;

        return (
            <Wootric 
                userIdentifier={userIdentifier} 
                userCreatedAt={userCreatedAt} 
                userName={userName} 
                isModetator={isModerator}
                isRegistered={isRegistered}
                planType={planType}
            />
        );
    }
            
    return (
        <Container container spacing={2} justifyContent="space-between" alignItems="center">
            <Grid size={{ xs: 2, sm: 3 }}></Grid>
            <PieChartContainer size={{ xs: 8, sm: 6 }}>
                <PieChart
                    style={{ fontFamily: "Roboto", fontSize: "10px" }} 
                    labelStyle={{ fill: '#fff' }}
                    label={({ dataEntry }) => dataEntry.title} 
                    startAngle={-90} 
                    animate={true} 
                    labelPosition={50+(pieData.length)}
                    data={chartData}
                />
            </PieChartContainer>
            <Grid size={{ xs: 2, sm: 3 }}></Grid>
            {props.roundCount >= 3 && renderWootric()}
        </Container>
    );
}